import React from "react";
import { useAuth } from "@/context/AuthContext";
import { UpgradePrompt } from "@/components/UpgradePrompt";
import { DisclaimerBanner } from "@/components/DisclaimerBanner";
import { Youtube, PlayCircle } from "lucide-react";

// Real TJR (Tyler J. Riches) trading education videos on YouTube.
const VIDEOS = [
  { id: "qJap-CZoV6g", title: "6 Years of Trading Knowledge in 20 Minutes", desc: "TJR condenses years of experience — market structure, liquidity, risk and mindset — into a fast beginner overview." },
  { id: "5pF0hhQ7V1I", title: "How to Trade", desc: "TJR walks through his core approach: reading structure, waiting for liquidity sweeps and finding an entry." },
  { id: "VdLKK1EQ92A", title: "Why New Traders Keep Failing", desc: "The mistakes beginners make and the concepts you need to understand before risking anything." },
  { id: "r6QaBbR5mz0", title: "How To Go Full Time With Trading", desc: "On discipline, risk management and the realistic path — with honest expectations, no hype." },
];

export default function Videos() {
  const { user } = useAuth();
  const isPro = user?.plan === "pro";
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-red-500/15 border border-red-500/25 grid place-items-center">
          <Youtube className="w-6 h-6 text-red-400" />
        </div>
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight">Video Lessons with TJR</h1>
          <p className="text-slate-400 text-sm mt-0.5">Learn how to approach trading in 2026 — market structure, liquidity, risk and psychology.</p>
        </div>
      </div>

      {!isPro ? (
        <UpgradePrompt title="Video lessons are a Pro feature" description="Watch curated TJR trading lessons. Unlock them with Pro (€3.99/month)." />
      ) : (
      <>
      <div className="bg-blue-500/10 border border-blue-500/25 rounded-xl px-4 py-3 text-sm text-slate-300 leading-relaxed">
        These are third-party educational videos from <span className="font-semibold text-blue-300">TJR (Tyler J. Riches)</span> on YouTube. They explain concepts — not guarantees. Nothing here is financial advice, and every method carries a risk of losing money.
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        {VIDEOS.map((v) => (
          <div key={v.id} data-testid={`video-${v.id}`} className="bg-[#0F172A] border border-slate-800 rounded-2xl overflow-hidden hover:border-blue-500/40 transition-colors">
            <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
              <iframe
                className="absolute inset-0 w-full h-full"
                src={`https://www.youtube-nocookie.com/embed/${v.id}`}
                title={v.title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                loading="lazy"
              />
            </div>
            <div className="p-5">
              <div className="flex items-start gap-2">
                <PlayCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-display font-semibold leading-snug">{v.title}</h3>
                  <p className="text-sm text-slate-400 mt-1 leading-relaxed">{v.desc}</p>
                </div>
              </div>
              <a href={`https://www.youtube.com/watch?v=${v.id}`} target="_blank" rel="noopener noreferrer"
                data-testid={`video-link-${v.id}`} className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-400 hover:text-blue-300 mt-3">
                Watch on YouTube
              </a>
            </div>
          </div>
        ))}
      </div>

      <DisclaimerBanner compact />
      </>
      )}
    </div>
  );
}
